import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BodensteckbriefEntry, ListenService } from '../../listenverwaltung/listen.service';
import { SteckbriefFensterComponent } from '../../listenverwaltung/steckbrief-fenster.component';
import { FbfPickerModalComponent } from './fbf-picker-modal.component';

/** Was das Auswahlfenster der Feinbodenform zurueckgibt. */
export interface FbfWahl {
	fbf: string;
	hbf: string;
}

/** Das Feld, das eine gewaehlte Feinbodenform aufnimmt (das Vorschlagsfeld der Bibliothek). */
export interface FbfFeld {
	onHelpValueSelected(wahl: FbfWahl): void;
}

/**
 * Die Steckbriefe zu einer Hauptbodenform, in der Reihenfolge des Katalogs.
 * Verglichen wird das Kuerzel genau so, wie es im Steckbrief steht - eine
 * unscharfe Suche gehoert in den Katalog-Leser, nicht in ein Fenster, das
 * „der Steckbrief zu diesem Feld" heisst.
 */
export function steckbriefeZurHbf(
	eintraege: readonly BodensteckbriefEntry[] | null | undefined,
	hbf: string | null | undefined,
): BodensteckbriefEntry[] {
	const kuerzel = (hbf ?? '').trim();
	if (!kuerzel) return [];
	return (eintraege ?? [])
		.filter(e => (e?.hauptbodenform?.abkuerzung ?? '').trim() === kuerzel)
		.sort((a, b) => (a.pdf ?? '').localeCompare(b.pdf ?? '') || (a.page ?? 0) - (b.page ?? 0));
}

/**
 * Die Knoepfe an den Bodenform-Feldern (Bodenaufnahme und Kurzprotokoll).
 *
 * Nutzer-Entscheidung 28.09.2026: Statt des (i) - am Tablet 7,5 x 11 Punkte gross
 * und dort keine Erklaerung, sondern ein Sprung - sitzt im Feld ein Knopf:
 * „Katalog" an der Hauptbodenform, „Liste" an der Feinbodenform.
 */
@Injectable({ providedIn: 'root' })
export class BodenformNachschlagenService {
	private modal = inject(NgbModal);
	private router = inject(Router);
	private listen = inject(ListenService);

	/**
	 * Katalog-Knopf an der Hauptbodenform.
	 *
	 * - Steckbrief vorhanden: als Fenster ueber der Maske, man bleibt in der Aufnahme.
	 * - Feld leer: die Uebersicht des Katalog-Lesers.
	 * - Kein Steckbrief zum Kuerzel (anders geschrieben, Moor, Liste nicht zu laden):
	 *   der Katalog-Leser mit dem Kuerzel als Suche. Er sucht breiter und springt
	 *   zum besten Treffer - so wie vorher das (i). Eine Sackgasse soll es nicht geben.
	 */
	async zeigeSteckbrief(hbf: string | null | undefined): Promise<void> {
		const kuerzel = (hbf ?? '').trim();
		if (!kuerzel) {
			await this.router.navigate(['/bodenformenkatalog']);
			return;
		}
		let treffer: BodensteckbriefEntry[] = [];
		try {
			const alle = (await this.listen.getList('Bodensteckbriefe')) as BodensteckbriefEntry[] | null;
			treffer = steckbriefeZurHbf(alle, kuerzel);
		} catch {
			treffer = [];
		}
		if (!treffer.length) {
			await this.router.navigate(['/bodenformenkatalog'], { queryParams: { search: kuerzel } });
			return;
		}
		const ref = this.modal.open(SteckbriefFensterComponent, { size: 'xl', scrollable: true, fullscreen: 'lg' });
		ref.componentInstance.hbf = kuerzel;
		ref.componentInstance.steckbriefe = treffer;
		// Wegklicken lehnt das Versprechen ab; ohne Faenger zaehlte das als Fehler.
		ref.result.then(() => undefined, () => undefined);
	}

	/**
	 * Listen-Knopf an der Feinbodenform: dasselbe Auswahlfenster wie vorher hinter
	 * dem (i). Die Wahl geht ueber das Feld selbst (`onHelpValueSelected`), also
	 * denselben Weg wie vorher: Das Feld meldet die Aenderung und reicht die
	 * Hauptbodenform nach oben weiter.
	 */
	waehleFeinbodenform(feld: FbfFeld, aktuell: string | undefined, hbfFilter: string | undefined): Promise<void> {
		const ref = this.modal.open(FbfPickerModalComponent, { centered: true, scrollable: true, size: 'lg' });
		ref.componentInstance.currentValue = aktuell;
		ref.componentInstance.hbfFilter = hbfFilter;
		return ref.result.then(
			(wahl: FbfWahl | undefined) => {
				if (wahl?.fbf) feld.onHelpValueSelected(wahl);
			},
			() => undefined,
		);
	}
}
